import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2, Lock } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputCls = "bg-white border-[#D9E0E6] text-[var(--ci-grey)] placeholder:text-[var(--ci-grey)] focus-visible:ring-2 focus-visible:ring-[var(--ci-blue)] focus-visible:ring-offset-0 focus-visible:border-[var(--ci-blue)] rounded-xl h-12";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FA] text-[var(--ci-grey)] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 font-tech text-[11px] tracking-[0.2em] uppercase text-[var(--ci-grey)] hover:text-[var(--ci-turquoise)] transition-colors mb-8" data-testid="login-back-link">
          <ArrowLeft size={14} /> Zurück zur Website
        </Link>
        <form onSubmit={submit} className="border border-[#E3E8EC] bg-white rounded-2xl p-8 lg:p-12 shadow-[0_4px_40px_rgba(17,24,39,0.06)]" data-testid="admin-login-form">
          <img src="/assets/hamm-logo.png" alt="Hamm Entwicklung + Produktion GmbH" className="h-11 w-auto mb-8" />
          <div className="flex items-center gap-3 mb-2">
            <span className="w-10 h-10 rounded-xl bg-[var(--ci-blue-light)] flex items-center justify-center"><Lock size={16} className="text-[var(--ci-blue)]" /></span>
            <h1 className="text-2xl font-bold tracking-tight text-[var(--ci-grey)]">Admin-Bereich</h1>
          </div>
          <p className="text-sm text-[var(--ci-grey)] mb-9">Anmelden, um Fertigungsanfragen und Zeichnungen zu verwalten.</p>

          {error && (
            <div className="mb-6 flex items-start gap-3 border border-[#FF3B30]/40 bg-[#FF3B30]/5 rounded-xl px-4 py-3" data-testid="admin-login-error">
              <AlertTriangle size={16} className="text-[#D92D20] mt-0.5 shrink-0" />
              <p className="text-sm text-[#D92D20]">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <Label htmlFor="admin-email" className="font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">E-Mail</Label>
              <Input id="admin-email" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@hamm-ep.de" className={`${inputCls} mt-2.5`} data-testid="admin-email-input" />
            </div>
            <div>
              <Label htmlFor="admin-password" className="font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">Passwort</Label>
              <Input id="admin-password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••" className={`${inputCls} mt-2.5`} data-testid="admin-password-input" />
            </div>
          </div>

          <button type="submit" disabled={loading} data-testid="admin-login-btn"
            className="mt-9 w-full flex items-center justify-center gap-3 h-12 rounded-full bg-[var(--ci-blue)] hover:bg-[var(--ci-turquoise)] disabled:opacity-60 text-white font-tech text-[11px] tracking-[0.22em] uppercase transition-all duration-200 active:scale-[0.99]">
            {loading ? (<><Loader2 size={15} className="animate-spin" /> Prüfen…</>) : "Anmelden"}
          </button>
        </form>
        <p className="mt-7 font-tech text-[10px] tracking-[0.2em] uppercase text-[#B9C2CA]">Hamm Entwicklung + Produktion GmbH</p>
      </div>
    </div>
  );
}
